import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Terminal, Zap, CheckCircle2 } from 'lucide-react';

interface ProcessingStats {
  charactersProcessed: number;
  entitiesFound: number;
  processingTime: number;
  operationsCount: number;
}

interface ProcessingResult {
  result: string;
  stats: Omit<ProcessingStats, 'operationsCount'>;
}

const BashEscapeUnescapeTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [mode, setMode] = useState<'escape' | 'unescape'>('escape');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<ProcessingStats>({
    charactersProcessed: 0,
    entitiesFound: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');

  const escapeBash = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Simple character-by-character escaping
    const escapeMap = new Map([
      ['\\', '\\\\'],
      ['"', '\\"'],
      ['$', '\\$'],
      ['`', '\\`'],
      ['!', '\\!'],
      [' ', '\\ '],
      ['\n', '\\n'],
      ['\r', '\\r'],
      ['\t', '\\t'],
      ['&', '\\&'],
      ['|', '\\|'],
      [';', '\\;'],
      ['<', '\\<'],
      ['>', '\\>'],
      ['(', '\\('],
      [')', '\\)'],
      ['[', '\\['],
      [']', '\\]'],
      ['{', '\\{'],
      ['}', '\\}'],
      ['*', '\\*'],
      ['?', '\\?'],
      ['~', '\\~'],
      ['#', '\\#']
    ]);

    // Handle single quotes separately (bash special case)
    const chars = Array.from(result);
    result = '';
    
    for (const char of chars) {
      if (char === "'") {
        result += "'\"'\"'";
        entitiesCount++;
      } else if (escapeMap.has(char)) {
        result += escapeMap.get(char);
        entitiesCount++;
      } else {
        result += char;
      }
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const unescapeBash = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Simple replacement patterns
    const unescapePatterns = [
      { pattern: /\\n/g, replacement: '\n' },
      { pattern: /\\r/g, replacement: '\r' },
      { pattern: /\\t/g, replacement: '\t' },
      { pattern: /\\ /g, replacement: ' ' },
      { pattern: /\\#/g, replacement: '#' },
      { pattern: /\\~/g, replacement: '~' },
      { pattern: /\\\?/g, replacement: '?' },
      { pattern: /\\\*/g, replacement: '*' },
      { pattern: /\\\{/g, replacement: '{' },
      { pattern: /\\\}/g, replacement: '}' },
      { pattern: /\\\[/g, replacement: '[' },
      { pattern: /\\\]/g, replacement: ']' },
      { pattern: /\\\(/g, replacement: '(' },
      { pattern: /\\\)/g, replacement: ')' },
      { pattern: /\\>/g, replacement: '>' },
      { pattern: /\\</g, replacement: '<' },
      { pattern: /\\;/g, replacement: ';' },
      { pattern: /\\\|/g, replacement: '|' },
      { pattern: /\\&/g, replacement: '&' },
      { pattern: /\\!/g, replacement: '!' },
      { pattern: /\\`/g, replacement: '`' },
      { pattern: /\\\$/g, replacement: '$' },
      { pattern: /\\"/g, replacement: '"' },
      { pattern: /\\\\/g, replacement: '\\' }
    ];

    // Handle single quotes first
    const singleQuotePattern = /'\"'\"'/g;
    const singleQuoteMatches = result.match(singleQuotePattern);
    if (singleQuoteMatches) {
      entitiesCount += singleQuoteMatches.length;
      result = result.replace(singleQuotePattern, "'");
    }

    // Apply all other unescaping patterns
    for (const { pattern, replacement } of unescapePatterns) {
      const matches = result.match(pattern);
      if (matches) {
        entitiesCount += matches.length;
        result = result.replace(pattern, replacement);
      }
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const processText = useCallback((): void => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapeBash : unescapeBash;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapeBash, unescapeBash]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const blob = new Blob([outputText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-bash.${mode === 'escape' ? 'sh' : 'txt'}`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      entitiesFound: 0,
      processingTime: 0,
      operationsCount: 0
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleBash = (): void => {
    setInputText(`#!/bin/bash
# Sample Bash script with special characters

USER_NAME="John O'Connor"
FILE_PATH="/home/user/My Documents/file with spaces.txt"
PRICE="$19.99"

function greet_user() {
    local name="$1"
    echo "Hello, $name! Welcome to the system."
    echo "Current date: $(date)"
}

if [ -f "$FILE_PATH" ]; then
    echo "File exists: $FILE_PATH"
else
    echo "File not found!"
fi

for file in *.txt; do
    echo "Processing: $file"
done

case "$1" in
    "start")
        echo "Starting service..."
        ;;
    "stop")
        echo "Stopping service..."
        ;;
    *)
        echo "Usage: $0 {start|stop}"
        ;;
esac

exit 0`);
  };

  const loadSampleEscaped = (): void => {
    setInputText(`\\#\\!/bin/bash
\\#\\ Sample\\ Bash\\ script\\ with\\ special\\ characters

USER_NAME=\\"John\\ O'"'"'Connor\\"
FILE_PATH=\\"/home/user/My\\ Documents/file\\ with\\ spaces.txt\\"
PRICE=\\"\\$19.99\\"

function\\ greet_user\\(\\)\\ \\{
\\ \\ \\ \\ local\\ name=\\"\\$1\\"
\\ \\ \\ \\ echo\\ \\"Hello,\\ \\$name\\!\\ Welcome\\ to\\ the\\ system.\\"
\\ \\ \\ \\ echo\\ \\"Current\\ date:\\ \\$\\(date\\)\\"
\\}

exit\\ 0`);
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-gray-500 to-gray-700 rounded-lg">
              <Terminal className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Bash Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced Bash shell string processor with command substitution support</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-gray-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape Bash</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-gray-500 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape Bash</span>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-gray-400">{stats.charactersProcessed.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.entitiesFound}</div>
                <div className="text-gray-300 text-sm">Escapes</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* Bash Escape Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Bash Escape Sequences & Shell Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Basic:</strong> backslash quote space newline tab
                </div>
                <div className="text-gray-300">
                  <strong>Variables:</strong> dollar backtick exclamation expansion
                </div>
                <div className="text-gray-300">
                  <strong>Operators:</strong> ampersand pipe semicolon redirection
                </div>
                <div className="text-gray-300">
                  <strong>Wildcards:</strong> asterisk question bracket brace tilde
                </div>
                <div className="text-gray-300">
                  <strong>Grouping:</strong> parentheses for subshells
                </div>
                <div className="text-gray-300">
                  <strong>Security:</strong> Command injection prevention
                </div>
                <div className="text-gray-300">
                  <strong>Safe:</strong> Shell metacharacter handling
                </div>
                <div className="text-gray-300">
                  <strong>Compatible:</strong> Bash 3.0+ standards
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Large input/output areas */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".sh,.bash,.zsh,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter Bash script to escape...' : 'Enter escaped Bash script to unescape...'}
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-gray-500 focus:ring-1 focus:ring-gray-500 outline-none resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {inputText.length.toLocaleString()}</span>
              <span>Lines: {inputText.split('\n').length}</span>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed Bash output will appear here..."
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {outputText.length.toLocaleString()}</span>
              <span>Lines: {outputText.split('\n').length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleBash}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Bash
            </button>
            <button
              onClick={loadSampleEscaped}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Escaped
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/#.*$/gm, '').trim())}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
            >
              Remove Comments
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\s+/g, ' ').trim())}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              Normalize Whitespace
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/^\s+/gm, ''))}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
            >
              Remove Indentation
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\$/g, '\\$').replace(/`/g, '\\`'))}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-sm"
            >
              Escape Variables
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/ /g, '\\ '))}
              className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors text-sm"
            >
              Escape Spaces
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BashEscapeUnescapeTool;